import { useState, useEffect } from "react";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";

import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import HelpTooltip from "components/HelpTooltip";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "components/layout/DashboardLayout";
import DashboardNavbar from "components/navigation/DashboardNavbar";
import GradientLineChart from "components/patterns/charts/GradientLineChart";
import { calculateServiceLife } from "common/utils";
import { useApiClient } from "common/ApiClientProvider";

function ServiceLife() {
  const { t } = useTranslation("pages");
  const { t: tHelp } = useTranslation("help");
  const apiClient = useApiClient();

  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(0);

  const [serviceLifeData, setServiceLifeData] = useState({});
  const [fpServiceLifeData, setFpServiceLifeData] = useState({});
  const [remainingBatteryYears, setRemainingBatteryYears] = useState({});
  const [fpRemainingBatteryYears, setFpRemainingBatteryYears] = useState({});
  const [loadingOpen, setLoadingOpen] = useState(false);
  const handleLoadingClose = () => setLoadingOpen(false);
  const handleLoadingOpen = () => setLoadingOpen(true);

  async function fetchSites() {
    const response = await apiClient.get("sites");
    setSites(response);
  }

  useEffect(() => {
    fetchSites();
  }, []);

  function selectChange(event) {
    setSelectedSite(event.target.value);
    fetchSiteData(event.target.value);
  }

  function getRemainingBatteryYears(labels, series) {
    return [95, 90, 85, 80].reduce((acc, threshold) => {
      const thresholdIndex = series.findIndex((value) => Number(value) <= threshold);
      return {
        ...acc,
        [threshold]: thresholdIndex === -1 ? "-" : labels[thresholdIndex],
      };
    }, {});
  }

  function renderRemainingBatteryCards(data) {
    return (
      <Grid container spacing={3} sx={{ justifyContent: "center" }}>
        {[95, 90, 85, 80].map((threshold) => (
          <Grid item xs={12} md={6} lg={3} key={threshold}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="body2" color="text">
                  {t("serviceLifePage.remainingBattery", { threshold })}
                </MDTypography>
                <MDBox mt={2} mb={1} lineHeight={0}>
                  <MDTypography variant="h3" fontWeight="bold">
                    {data[threshold] ?? "-"}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  async function loadSiteData(siteId) {
    const response = await apiClient.get("get_trend_result", {
      query: { system_type: "ongrid" },
    });
    const displaySite = response.sites.filter((site) => site.site == siteId)[0];
    calculateServiceLife(displaySite);

    const yearlabels = [];
    for (let i = 0; i <= displaySite.years; i += 1) {
      yearlabels.push(displaySite.installed_year + i);
    }

    setServiceLifeData({
      labels: yearlabels,
      datasets: [
        {
          label: t("serviceLifePage.degradation"),
          color: "dark",
          data: displaySite.service_life_series,
        },
      ],
    });
    setRemainingBatteryYears(getRemainingBatteryYears(yearlabels, displaySite.service_life_series));

    const fpYearlabels = [];
    for (let i = 0; i <= displaySite.fp_years; i += 1) {
      fpYearlabels.push(displaySite.installed_year + i);
    }

    setFpServiceLifeData({
      labels: fpYearlabels,
      datasets: [
        {
          label: t("serviceLifePage.degradation"),
          color: "dark",
          data: displaySite.fp_service_life_series,
        },
      ],
    });
    setFpRemainingBatteryYears(
      getRemainingBatteryYears(fpYearlabels, displaySite.fp_service_life_series)
    );
  }

  async function fetchSiteData(siteId) {
    handleLoadingOpen();
    try {
      await loadSiteData(siteId);
    } finally {
      handleLoadingClose();
    }
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={loadingOpen}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <MDBox pb={3}>
        <MDBox p={5}>
          <FormControl fullWidth>
            <InputLabel id="sites-label">{t("serviceLifePage.sites")}</InputLabel>
            <Select
              labelId="sites-label"
              id="sites-select"
              value={selectedSite}
              label={t("serviceLifePage.selectSite")}
              sx={{ minHeight: "50px", maxWidth: "200px" }}
              onChange={selectChange}
            >
              {sites.map((site, index) => (
                <MenuItem key={index} value={site.id}>
                  {site.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </MDBox>
      </MDBox>
      <MDBox pb={3}>
        <MDBox mt={8} position="relative">
          <HelpTooltip
            title={tHelp("serviceLife.serviceLife")}
            sx={{ position: "absolute", top: 0, right: 24, zIndex: 2 }}
          />
          <MDTypography mt={2} variant="h3" align="center" sx={{ color: "#484731" }}>
            {t("serviceLifePage.title")}
          </MDTypography>
        </MDBox>
        <MDBox mt={4} px={5}>
          {renderRemainingBatteryCards(remainingBatteryYears)}
        </MDBox>
        <MDBox mt={4} display="flex" justifyContent="center" alignItems="center">
          <MDBox width="60rem">
            <GradientLineChart title="" chart={serviceLifeData} yUnit="%" />
          </MDBox>
        </MDBox>
      </MDBox>
      <MDBox pb={3}>
        <MDBox mt={8} position="relative">
          <HelpTooltip
            title={tHelp("serviceLife.fpServiceLife")}
            sx={{ position: "absolute", top: 0, right: 24, zIndex: 2 }}
          />
          <MDTypography mt={2} variant="h3" align="center" sx={{ color: "#484731" }}>
            {t("serviceLifePage.fpTitle")}
          </MDTypography>
        </MDBox>
        <MDBox mt={4} px={5}>
          {renderRemainingBatteryCards(fpRemainingBatteryYears)}
        </MDBox>
        <MDBox mt={4} display="flex" justifyContent="center" alignItems="center">
          <MDBox width="60rem">
            <GradientLineChart title="" chart={fpServiceLifeData} />
          </MDBox>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}

export default ServiceLife;
